// Moved from `src/hooks/mutation/clinicalTaskQueryCache.ts` (now a shim) with
// the D2 family (ARCFE-945). Keys now come from `/contracts` — the three query
// hooks and this module agree on cache identity through one definition site.
import type { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

import {
  withTaskAssignedToUser,
  type ClinicalTaskUserOwner,
} from '../../contracts/assignee';
import {
  GET_CLINICAL_TASKS,
  GET_CLINICAL_TASKS_BY_INBOX_MESSAGE,
  GET_CLINICAL_TASKS_BY_SESSION_ID,
} from '../../contracts/keys';
import { withClinicalTaskStatus } from '../../contracts/status';
import type { GetClinicalTasksResponse } from '../../contracts/types';
import { withTaskDelegatedToHeidi } from '../../contracts/work-project-link';

/**
 * Every cache key a clinical task can be read through. The same task is
 * reachable from the global list, from its session, and from the inbox message
 * it was created off, so a mutation that refreshes only some of these leaves
 * the others rendering a stale row.
 *
 * Held here rather than per-mutation so that adding a surface means adding a
 * key to this list, not re-auditing all five task mutations for a copy of it.
 *
 * Order carries no meaning — the keys are distinct, so every consumer restores
 * or updates its own entry. Append freely.
 */
const CLINICAL_TASK_QUERY_KEYS = [
  GET_CLINICAL_TASKS,
  GET_CLINICAL_TASKS_BY_SESSION_ID,
  GET_CLINICAL_TASKS_BY_INBOX_MESSAGE,
];

/** Refresh every clinical-task view; call from a mutation's `onSettled`. */
export function invalidateClinicalTaskQueries(queryClient: QueryClient): void {
  for (const queryKey of CLINICAL_TASK_QUERY_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [queryKey] });
  }
}

/** Stop in-flight reads of every clinical-task view before an optimistic write. */
export async function cancelClinicalTaskQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    CLINICAL_TASK_QUERY_KEYS.map((queryKey) =>
      queryClient.cancelQueries({ queryKey: [queryKey] })
    )
  );
}

/**
 * Snapshot every clinical-task view ahead of an optimistic write.
 *
 * The result does double duty at all three call sites: it is the rollback
 * record `onError` replays, and it is the set of cache entries the optimistic
 * update is applied over. A surface missing from the list gets neither.
 */
export function snapshotClinicalTaskQueries(
  queryClient: QueryClient
): [QueryKey, GetClinicalTasksResponse | undefined][] {
  return CLINICAL_TASK_QUERY_KEYS.flatMap((queryKey) =>
    queryClient.getQueriesData<GetClinicalTasksResponse>({ queryKey: [queryKey] })
  );
}

/** Replay a snapshot taken before an optimistic write. */
export function restoreClinicalTaskQueries(
  queryClient: QueryClient,
  snapshots: [QueryKey, GetClinicalTasksResponse | undefined][]
): void {
  for (const [key, data] of snapshots) {
    queryClient.setQueryData(key, data);
  }
}

/** Paint the WORK_PROJECT link onto every cached clinical-task list. */
export function applyDelegatedToHeidiToClinicalTaskQueries(
  queryClient: QueryClient,
  snapshots: [QueryKey, GetClinicalTasksResponse | undefined][],
  taskId: string,
  projectId: string
): void {
  for (const [queryKey] of snapshots) {
    queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, (old) => {
      if (!old?.tasks) return old;
      return {
        ...old,
        tasks: old.tasks.map((task) => withTaskDelegatedToHeidi(task, taskId, projectId)),
      };
    });
  }
}

/** Paint a user onto the owner slot of every cached clinical-task list. */
export function applyAssigneeToClinicalTaskQueries(
  queryClient: QueryClient,
  snapshots: [QueryKey, GetClinicalTasksResponse | undefined][],
  taskId: string,
  owner: ClinicalTaskUserOwner
): void {
  for (const [queryKey] of snapshots) {
    queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, (old) => {
      if (!old?.tasks) return old;
      return {
        ...old,
        tasks: old.tasks.map((task) => withTaskAssignedToUser(task, taskId, owner)),
      };
    });
  }
}

/** Paint a status onto one task in every cached clinical-task list. */
export function applyStatusToClinicalTaskQueries(
  queryClient: QueryClient,
  snapshots: [QueryKey, GetClinicalTasksResponse | undefined][],
  taskId: string,
  status: ClinicalTaskStatus
): void {
  for (const [queryKey] of snapshots) {
    queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, (old) => {
      if (!old?.tasks) return old;
      return {
        ...old,
        tasks: old.tasks.map((task) => withClinicalTaskStatus(task, taskId, status)),
      };
    });
  }
}
